/*
  MyMarket.jsx
  ----------------
  Interface d'affichage des marchés.
*/
import { useState, useEffect } from "react";
import { Stack, Card, Text, Group, Button, Center, TextInput, Pagination, Badge, Title, Loader } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../tools/Context";
import { get } from "../tools/Request";

export default function MyMarket() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");

  const [markets, setMarkets] = useState([]);
  const [loadingMarkets, setLoadingMarkets] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [notifications, setNotifications] = useState({});
  const itemsPerPage = 5;
  const { token, isratp } = useUser();

  useEffect(() => {
    const fetchMarkets = async () => {
      setLoadingMarkets(true);
      try {
        const response = await get("market", null, token);
        setMarkets(response);
      } catch (error) {
        console.error("Error fetching markets:", error);
      } finally {
        setLoadingMarkets(false);
      }
    };

    const fetchNotifications = async () => {
      try {
        const response = await get("notification/notifications", null, token);
        const notificationCounts = response.reduce((acc, notification) => {
          acc[notification.market_id] = (acc[notification.market_id] || 0) + 1;
          return acc;
        }, {});
        setNotifications(notificationCounts);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchMarkets();
    fetchNotifications();
  }, [token]);

  useEffect(() => {
    if (searchQuery) {
      setSearchParams({ search: searchQuery });
    } else {
      setSearchParams({});
    }
  }, [searchQuery, setSearchParams]);

  const filteredMarkets = markets.filter(
    (market) =>
      (market.market_number && market.market_number.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (market.market_description && market.market_description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const indexOfLastMarket = currentPage * itemsPerPage;
  const indexOfFirstMarket = indexOfLastMarket - itemsPerPage;
  const currentMarkets = filteredMarkets.slice(indexOfFirstMarket, indexOfLastMarket);

  const handleViewDetails = (id) => {
    navigate(`/MarketDisplay?id=${id}`);
  };

  return (
    <>
      <Center>
        <Title>Mes marchés</Title>
      </Center>
      <Stack spacing="lg" mt="md">
        <Center>
          <TextInput
            icon={<IconSearch />}
            placeholder="Recherchez un marché..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: "100%" }}
          />
        </Center>

        {loadingMarkets ? (
          <Center mt="xl">
            <Loader color={isratp ? "#0a0082" : "#ff7e2e"} />
          </Center>
        ) : (
          <>
            {currentMarkets.map((market) => (
              <Center key={market.id}>
                <Card shadow="sm" padding="lg" radius="md" withBorder w="100%">
                  <Group justify="space-between">
                    <Group>
                      <Text weight={500}>{market.market_number}</Text>
                      <Text weight={500}>{market.pilot_direction}</Text>
                      <Text size="sm" color="dimmed">
                        {market.supplier_name}
                      </Text>
                    </Group>
                    <Group>
                      <Badge color={market.market_status === "Ouvert" ? "#00aa91" : "#e2231a"}>
                        {market.market_status}
                      </Badge>
                      {notifications[market.id] > 0 && (
                        <Badge color={isratp ? "#0a0082" : "#ff7e2e"}>
                          {notifications[market.id]} {notifications[market.id] > 1 ? "notifications" : "notifiaction"}
                        </Badge>
                      )}
                    </Group>
                  </Group>
                  <Text size="sm" color="dimmed">
                    {market.market_description}
                  </Text>
                  <Button
                    variant="light"
                    color={isratp ? "#0a0082" : "#ff7e2e"}
                    fullWidth
                    mt="md"
                    onClick={() => handleViewDetails(market.id)}
                  >
                    Voir les détails
                  </Button>
                </Card>
              </Center>
            ))}

            {filteredMarkets.length === 0 && (
              <Text size="sm" color="dimmed">
                Aucun marché trouvé.
              </Text>
            )}

            <Center>
              <Pagination
                total={Math.ceil(filteredMarkets.length / itemsPerPage)}
                value={currentPage}
                color={isratp ? "#0a0082" : "#ff7e2e"}
                onChange={setCurrentPage}
              />
            </Center>
          </>
        )}
      </Stack>
    </>
  );
}
